/**
 * MediaUploadIcon Component
 * Visible button to toggle media upload/management for questions and options.
 * Uses upload arrow icon with grey background for better visibility.
 */

import React, { useState } from 'react';
import { Upload } from 'lucide-react';

interface MediaUploadIconProps {
  /** Whether media exists */
  hasMedia: boolean;
  /** Number of media items */
  mediaCount: number;
  /** Click handler to toggle media panel */
  onClick: () => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Optional custom class name */
  className?: string;
}

const MediaUploadIcon: React.FC<MediaUploadIconProps> = ({
  hasMedia,
  mediaCount,
  onClick,
  disabled = false,
  className = ''
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const tooltipText = hasMedia 
    ? `${mediaCount} image${mediaCount !== 1 ? 's' : ''} attached - Click to manage` 
    : 'Upload images';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onClick();
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={disabled}
        className={`
          relative inline-flex items-center justify-center
          w-9 h-9 rounded-lg transition-all border
          ${disabled 
            ? 'opacity-50 cursor-not-allowed bg-gray-100 border-gray-200' 
            : hasMedia
              ? 'bg-teal-50 border-teal-200 hover:bg-teal-100 hover:border-teal-300 cursor-pointer'
              : 'bg-gray-100 border-gray-200 hover:bg-gray-200 hover:border-gray-300 cursor-pointer'
          }
          ${className}
        `}
      >
        <Upload 
          className={`w-5 h-5 ${hasMedia ? 'text-teal-600' : 'text-gray-500'}`} 
        />
        
        {/* Count Badge */}
        {hasMedia && mediaCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
            {mediaCount}
          </span>
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-50">
          {tooltipText}
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

export default MediaUploadIcon;

